import {
  aspectLabel,
  type DocumentSummary,
  getPreset,
  LIBRARY_TEMPLATES,
  type LibraryTemplate,
  libraryTemplateCategory,
  libraryTemplateHasDevice,
  libraryTemplateSearchFields,
  PRESET_CATEGORY_LABELS,
  type PresetCategory,
  searchList,
  TEMPLATE_TOPIC_LABELS,
  TEMPLATE_TOPICS,
  type TemplateTopic,
  VIDEO_PRESETS,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Clapperboard,
  ExternalLink,
  LayoutTemplate,
  PencilRuler,
  Plus,
  Search,
  Undo2,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { PresetIcon } from "../components/document-card";
import {
  LibraryTemplateTile,
  StylePicker,
  TemplateBrandNote,
  UseLibraryTemplateDialog,
  useTemplateBrand,
} from "../components/library-templates";
import { Button } from "../components/ui/button";
import { EmptyState, PageHeader, Skeleton, skeletonKeys } from "../components/ui/card";
import { ContextMenu } from "../components/ui/context-menu";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import { DropdownItem, DropdownSeparator } from "../components/ui/dropdown";
import { Field, Input, Select } from "../components/ui/input";
import { orpc } from "../lib/api";
import { useOrgId, useOrgRole } from "../lib/org";
import { cn, errorMessage } from "../lib/utils";

type Template = DocumentSummary & { projectName: string };

const FILTERS: (PresetCategory | "all")[] = ["all", "social", "reel", "print"];
/** Device mockups: all templates, only those with a device, or only those without. */
type MockupFilter = "all" | "with" | "without";
const MOCKUP_FILTERS: { id: MockupFilter; label: string }[] = [
  { id: "all", label: "Alle" },
  { id: "with", label: "Mit Geräte-Mockup" },
  { id: "without", label: "Ohne Mockup" },
];
/** Sections of the library under "Alle". */
const SECTION_ORDER: PresetCategory[] = ["social", "reel", "print"];
/** Short names of the video formats for the preview chips. */
const VIDEO_FORMAT_NAMES: Record<string, string> = {
  reel: "Reel & TikTok",
  "video-feed": "Feed",
  "video-square": "Quadrat",
  "video-landscape": "YouTube",
};

/** Template tile with thumbnail; used in the gallery and the "new file" dialog. */
export function TemplateTile({
  template,
  selected,
  onClick,
}: {
  template: Template;
  selected?: boolean;
  onClick: () => void;
}) {
  const preset = getPreset(template.formatPreset);
  const ratio = template.width / template.height;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group overflow-hidden rounded-xl border bg-white text-left transition hover:border-brand-300 hover:shadow-md",
        selected ? "border-brand-500 ring-2 ring-brand-500/20" : "border-zinc-200",
      )}
    >
      <div className="flex h-36 items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-50 p-3">
        {template.thumbnailUrl ? (
          <img
            src={template.thumbnailUrl}
            alt=""
            loading="lazy"
            className="max-h-full max-w-full rounded-sm shadow-sm ring-1 ring-black/5"
          />
        ) : (
          <div
            className="flex items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-400"
            style={
              ratio >= 1 ? { width: 100, height: 100 / ratio } : { height: 100, width: 100 * ratio }
            }
          >
            <PresetIcon presetId={template.formatPreset} />
          </div>
        )}
      </div>
      <div className="border-t border-zinc-100 px-3 py-2.5">
        <p className="truncate text-sm font-medium text-zinc-900">{template.title}</p>
        <p className="truncate text-xs text-zinc-500">
          {preset?.label ?? template.formatPreset} · {template.projectName}
        </p>
      </div>
    </button>
  );
}

export function TemplatesPage() {
  const orgId = useOrgId();
  const { can } = useOrgRole(orgId);
  const templates = useQuery(orpc.templates.list.queryOptions({ input: { orgId } }));
  const brand = useTemplateBrand(orgId);
  const [filter, setFilter] = useState<PresetCategory | "all">("all");
  const [topic, setTopic] = useState<TemplateTopic | "all">("all");
  const [mockup, setMockup] = useState<MockupFilter>("all");
  const [style, setStyle] = useState<string | undefined>(undefined);
  /** Video filter: preview (and create) the video templates in this format. */
  const [videoFormat, setVideoFormat] = useState<string | undefined>(undefined);
  const [using, setUsing] = useState<Template | null>(null);
  const [usingLibrary, setUsingLibrary] = useState<LibraryTemplate | null>(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const unmark = useMutation(
    orpc.documents.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.templates.key() });
        await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
        toast.success("Nicht mehr als Vorlage markiert");
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const ownItems = (template: Template) => (
    <>
      {can("document.create") ? (
        <DropdownItem onSelect={() => setUsing(template)}>
          <Plus /> Neue Datei daraus …
        </DropdownItem>
      ) : null}
      <DropdownItem
        onSelect={() =>
          void navigate({
            to: "/o/$orgId/documents/$documentId",
            params: { orgId, documentId: template.id },
          })
        }
      >
        <PencilRuler /> Vorlage bearbeiten
      </DropdownItem>
      <DropdownItem
        onSelect={() => window.open(`/o/${orgId}/documents/${template.id}`, "_blank", "noopener")}
      >
        <ExternalLink /> In neuem Tab öffnen
      </DropdownItem>
      {can("document.update") ? (
        <>
          <DropdownSeparator />
          <DropdownItem
            onSelect={() => unmark.mutate({ orgId, documentId: template.id, isTemplate: false })}
          >
            <Undo2 /> Keine Vorlage mehr
          </DropdownItem>
        </>
      ) : null}
    </>
  );
  // Opened from the search (⌘K): the "use template" dialog right away.
  const params = useSearch({ strict: false }) as { use?: string; library?: string };
  useEffect(() => {
    if (params.library) {
      const template = LIBRARY_TEMPLATES.find((entry) => entry.id === params.library);
      if (template && can("document.create")) setUsingLibrary(template);
    } else if (params.use) {
      const template = templates.data?.find((entry) => entry.id === params.use);
      if (!template) return;
      if (can("document.create")) setUsing(template);
    } else return;
    void navigate({ to: "/o/$orgId/templates", params: { orgId }, search: {}, replace: true });
  }, [params.library, params.use, templates.data, can, navigate, orgId]);
  const own = searchList(
    (templates.data ?? []).filter(
      (template) => filter === "all" || getPreset(template.formatPreset)?.category === filter,
    ),
    query,
    (template) => [template.title, template.projectName, getPreset(template.formatPreset)?.label],
  );
  const matching = searchList(
    LIBRARY_TEMPLATES.filter(
      (template) =>
        (filter === "all" || libraryTemplateCategory(template) === filter) &&
        (topic === "all" || template.topic === topic) &&
        (mockup === "all" || libraryTemplateHasDevice(template) === (mockup === "with")),
    ),
    query,
    libraryTemplateSearchFields,
  );
  const previewFormat = filter === "reel" ? videoFormat : undefined;
  // In a chosen video format, the templates designed for it come first.
  const library = previewFormat
    ? [
        ...matching.filter((template) => template.presetId === previewFormat),
        ...matching.filter((template) => template.presetId !== previewFormat),
      ]
    : matching;
  // "Alle": one section per kind of format.
  const sections =
    filter === "all"
      ? SECTION_ORDER.map((category) => ({
          category,
          templates: library.filter((template) => libraryTemplateCategory(template) === category),
        })).filter((section) => section.templates.length > 0)
      : [{ category: filter, templates: library }];
  const counts = (category: PresetCategory | "all") =>
    category === "all"
      ? LIBRARY_TEMPLATES.length
      : LIBRARY_TEMPLATES.filter((template) => libraryTemplateCategory(template) === category)
          .length;
  const previewStyle = brand?.tokens ? undefined : style;
  const open = (template: LibraryTemplate) =>
    can("document.create") ? setUsingLibrary(template) : undefined;
  return (
    <>
      <PageHeader
        title="Vorlagen"
        description="Fertige Layouts für Posts, Stories, Flyer, Plakate, Reels und YouTube-Videos – anpassen, Fotos hineinziehen, fertig."
      />
      <div className="mb-6">
        <TemplateBrandNote orgId={orgId} brand={brand}>
          <StylePicker value={style} onChange={setStyle} />
        </TemplateBrandNote>
      </div>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-zinc-400" />
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Vorlagen durchsuchen, z. B. „Rabatt“, „Podcast“ oder „Rezept“"
          aria-label="Vorlagen durchsuchen"
          className="pl-9"
        />
      </div>
      <div className="mb-3 flex flex-wrap gap-1">
        {FILTERS.map((entry) => (
          <button
            key={entry}
            type="button"
            onClick={() => setFilter(entry)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100",
              filter === entry && "bg-zinc-900 text-white hover:bg-zinc-900",
            )}
          >
            {entry === "reel" ? <Clapperboard className="size-4" /> : null}
            {entry === "all" ? "Alle" : PRESET_CATEGORY_LABELS[entry]}
            <span className={cn("text-xs", filter === entry ? "text-white/70" : "text-zinc-400")}>
              {counts(entry)}
            </span>
          </button>
        ))}
      </div>
      {filter === "reel" ? (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs text-zinc-500">Vorschau im Format:</span>
          {[undefined, ...VIDEO_PRESETS].map((preset) => (
            <button
              key={preset?.id ?? "own"}
              type="button"
              onClick={() => setVideoFormat(preset?.id)}
              title={preset?.label ?? "Jede Vorlage in dem Format, für das sie gestaltet ist"}
              className={cn(
                "rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-600 hover:border-zinc-300",
                videoFormat === preset?.id && "border-zinc-900 bg-zinc-900 text-white",
              )}
            >
              {preset
                ? `${aspectLabel(preset.width, preset.height)} · ${VIDEO_FORMAT_NAMES[preset.id] ?? preset.label}`
                : "Original"}
            </button>
          ))}
        </div>
      ) : null}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs text-zinc-500">Geräte:</span>
        {MOCKUP_FILTERS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setMockup(entry.id)}
            title={
              entry.id === "with"
                ? "Vorlagen mit Handy, Tablet, Laptop oder Browserfenster – Screenshot oder Bildschirmaufnahme aufs Gerät ziehen"
                : undefined
            }
            className={cn(
              "rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-600 hover:border-zinc-300",
              mockup === entry.id && "border-zinc-900 bg-zinc-900 text-white",
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>
      <div className="mb-6 flex flex-wrap gap-1.5">
        {(["all", ...TEMPLATE_TOPICS] as const).map((entry) => (
          <button
            key={entry}
            type="button"
            onClick={() => setTopic(entry)}
            className={cn(
              "rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-600 hover:border-zinc-300",
              topic === entry && "border-brand-500 bg-brand-50 text-brand-700",
            )}
          >
            {entry === "all" ? "Alle Themen" : TEMPLATE_TOPIC_LABELS[entry]}
          </button>
        ))}
      </div>

      {topic === "all" && own.length > 0 ? (
        <section className="mb-10">
          <h2 className="mb-3 text-sm font-semibold text-zinc-700">Eigene Vorlagen</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {own.map((template) => (
              <ContextMenu key={template.id} menu={ownItems(template)}>
                <TemplateTile
                  template={template}
                  onClick={() => (can("document.create") ? setUsing(template) : undefined)}
                />
              </ContextMenu>
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-zinc-700">Vorlagen-Bibliothek</h2>
          <p className="text-xs text-zinc-500">
            {filter === "reel"
              ? "Videovorlagen passen in jedes Videoformat – mit der Maus darüber fahren spielt die Animation ab."
              : "Eigene Vorlagen: eine Datei öffnen und im Menü „Als Vorlage markieren“ wählen."}
          </p>
        </div>
        {!brand ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {skeletonKeys(8).map((key) => (
              <Skeleton key={key} className="h-60" />
            ))}
          </div>
        ) : library.length === 0 ? (
          <EmptyState
            icon={<LayoutTemplate />}
            title="Keine passende Vorlage"
            description={
              query.trim()
                ? `Keine Vorlage passt zu „${query.trim()}“.`
                : "Für diese Kombination aus Format, Thema und Gerät gibt es noch keine Vorlage."
            }
          />
        ) : (
          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.category}>
                {filter === "all" ? (
                  <div className="mb-3 flex items-baseline justify-between gap-2">
                    <h3 className="text-xs font-semibold text-zinc-700">
                      {PRESET_CATEGORY_LABELS[section.category]} · {section.templates.length}
                    </h3>
                    {sections.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setFilter(section.category)}
                        className="text-xs font-medium text-brand-700 hover:underline"
                      >
                        Nur {PRESET_CATEGORY_LABELS[section.category]} zeigen
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                  {section.templates.map((template) => (
                    <ContextMenu
                      key={template.id}
                      menu={
                        can("document.create") ? (
                          <DropdownItem onSelect={() => open(template)}>
                            <Plus /> Neue Datei daraus …
                          </DropdownItem>
                        ) : null
                      }
                    >
                      <LibraryTemplateTile
                        template={template}
                        brand={brand}
                        style={previewStyle}
                        presetId={previewFormat}
                        onClick={() => open(template)}
                      />
                    </ContextMenu>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      {using ? (
        <UseTemplateDialog orgId={orgId} template={using} onClose={() => setUsing(null)} />
      ) : null}
      {usingLibrary ? (
        <UseLibraryTemplateDialog
          orgId={orgId}
          template={usingLibrary}
          style={previewStyle}
          defaultPresetId={previewFormat}
          onClose={() => setUsingLibrary(null)}
        />
      ) : null}
    </>
  );
}

function UseTemplateDialog({
  orgId,
  template,
  onClose,
}: {
  orgId: string;
  template: Template;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projects = useQuery(orpc.projects.list.queryOptions({ input: { orgId } }));
  const [projectId, setProjectId] = useState(template.projectId);
  const [title, setTitle] = useState("");
  const create = useMutation(
    orpc.documents.create.mutationOptions({
      onSuccess: async (document) => {
        await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
        await queryClient.invalidateQueries({ queryKey: orpc.projects.key() });
        onClose();
        await navigate({
          to: "/o/$orgId/documents/$documentId",
          params: { orgId, documentId: document.id },
        });
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate({
      orgId,
      projectId,
      templateId: template.id,
      title: title.trim() || template.title,
    });
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`„${template.title}“ verwenden`}
        description="Legt eine Kopie der Vorlage an. Die Vorlage selbst bleibt unverändert."
      >
        <form onSubmit={submit} className="space-y-4">
          <Field label="Titel">
            <Input
              autoFocus
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={template.title}
            />
          </Field>
          <Field label="Projekt" hint="Die Datei übernimmt das Brand Kit dieses Projekts.">
            <Select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              {projects.data?.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Select>
          </Field>
          <DialogFooter>
            <Button type="submit" loading={create.isPending}>
              Datei anlegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
